import React, { Fragment, useEffect, useState } from "react";
import { Alert, Button, Card, CardBody, CardHeader, Col, FormGroup, Input, InputGroup, InputGroupText, Label, Row, Spinner } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { Eye, EyeOff } from "react-feather";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs";
import { AesDecrypt, AesEncrypt, keyPromise } from "../../../Service/crypto";
import api from "../../../api";

type Security = "tls" | "ssl" | "none";

interface MailForm {
  provider: string;
  host: string;
  port: string;
  security: Security;
  username: string;
  password: string;
  from_email: string;
  from_name: string;
}

type Errors = Partial<Record<keyof MailForm | "test_email", string>>;

// Common SMTP providers - picking one fills host / port / security. "Custom" accepts any server.
const PROVIDERS: { value: string; label: string; host: string; port: number; security: Security; hint?: string }[] = [
  { value: "office365", label: "Microsoft 365 / Office 365", host: "smtp.office365.com", port: 587, security: "tls", hint: "Enable 'Authenticated SMTP' for the mailbox in the Microsoft 365 admin center. Use an app password if the mailbox has MFA." },
  { value: "outlook", label: "Outlook.com / Hotmail", host: "smtp-mail.outlook.com", port: 587, security: "tls" },
  { value: "gmail", label: "Gmail / Google Workspace", host: "smtp.gmail.com", port: 587, security: "tls", hint: "Turn on 2-Step Verification and use a 16-character App Password - the normal Gmail password will not work." },
  { value: "zoho", label: "Zoho Mail", host: "smtp.zoho.com", port: 465, security: "ssl", hint: "Accounts in India use smtp.zoho.in. Use an app-specific password if 2FA is on." },
  { value: "yahoo", label: "Yahoo Mail", host: "smtp.mail.yahoo.com", port: 465, security: "ssl", hint: "Generate an app password in Yahoo account security." },
  { value: "ses", label: "Amazon SES", host: "email-smtp.us-east-1.amazonaws.com", port: 587, security: "tls", hint: "Use the SMTP credentials created in the SES console and change the region in the host if needed." },
  { value: "sendgrid", label: "SendGrid", host: "smtp.sendgrid.net", port: 587, security: "tls", hint: "Username is literally 'apikey'; the password is your SendGrid API key." },
  { value: "mailgun", label: "Mailgun", host: "smtp.mailgun.org", port: 587, security: "tls" },
  { value: "godaddy", label: "GoDaddy Workspace", host: "smtpout.secureserver.net", port: 465, security: "ssl" },
  { value: "custom", label: "Custom SMTP server", host: "", port: 587, security: "tls", hint: "Enter the SMTP details given by your mail provider or hosting (cPanel, etc.)." },
];

const SECURITY_OPTIONS = [
  { value: "tls", label: "TLS (STARTTLS) - usually port 587" },
  { value: "ssl", label: "SSL - usually port 465" },
  { value: "none", label: "None - usually port 25 (not recommended)" },
];

const emptyForm: MailForm = {
  provider: "office365", host: "smtp.office365.com", port: "587", security: "tls",
  username: "", password: "", from_email: "", from_name: "",
};

const callApi = async (url: string, payload: object) => {
  const key = await keyPromise;
  const EncParams = await AesEncrypt(key, payload);
  const response = await api.fetch(url, { method: "POST", body: JSON.stringify({ EncParams }) });
  if (response.status === 401) throw new Error("Your session has expired. Please sign in again.");
  return AesDecrypt(key, await response.json());
};

const MailConfiguration: React.FC = () => {
  const [form, setForm] = useState<MailForm>(emptyForm);
  const [hasPassword, setHasPassword] = useState(false);
  const [meta, setMeta] = useState<{ is_configured?: boolean; using_env_fallback?: boolean; updated_by?: string; updated_at?: string }>({});
  const [errors, setErrors] = useState<Errors>({});
  const [testEmail, setTestEmail] = useState(localStorage.getItem("emailId") || "");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await callApi("api/MailConfiguration/get", {});
      if (res?.error) throw new Error(res.error);
      if (res.is_configured) {
        setForm({
          provider: res.provider || "custom", host: res.host || "", port: String(res.port || ""),
          security: res.security || "tls", username: res.username || "", password: "",
          from_email: res.from_email || "", from_name: res.from_name || "",
        });
      }
      setHasPassword(!!res.has_password);
      setMeta(res);
    } catch (e: any) {
      Swal.fire({ title: "Mail Configuration", text: e?.message || "Unable to load the mail configuration.", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (field: keyof MailForm, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const onProviderChange = (value: string) => {
    const p = PROVIDERS.find((x) => x.value === value);
    if (!p) return;
    setForm((f) => ({ ...f, provider: p.value, host: p.host || (p.value === "custom" ? f.host : ""), port: String(p.port), security: p.security }));
    setErrors({});
  };

  const validate = (withTest = false): Errors => {
    const e: Errors = {};
    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!form.host.trim()) e.host = "SMTP host is required.";
    const port = Number(form.port);
    if (!Number.isInteger(port) || port < 1 || port > 65535) e.port = "Enter a valid port (e.g. 587 or 465).";
    if (!emailRe.test(form.from_email.trim())) e.from_email = "Enter a valid sender email address.";
    if (form.username.trim() && !form.password && !hasPassword) e.password = "Password is required.";
    if (withTest && !emailRe.test(testEmail.trim())) e.test_email = "Enter a valid email address.";
    return e;
  };

  const payload = () => ({ ...form, port: Number(form.port) });

  const onSave = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const res = await callApi("api/MailConfiguration/save", payload());
      if (res?.success) {
        await Swal.fire({ title: "Mail Configuration", html: `<span style="color: green;">${res.message}</span>`, icon: "success" });
        load();
      } else {
        setErrors(res?.errors || {});
        Swal.fire({ title: "Could not save", text: res?.message || "Unable to save the mail configuration.", icon: "error" });
      }
    } catch (err: any) {
      Swal.fire({ title: "Could not save", text: err?.message || "Unable to reach the server.", icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    const e = validate(true);
    setErrors(e);
    if (Object.keys(e).length) return;
    setTesting(true);
    try {
      const res = await callApi("api/MailConfiguration/test", { ...payload(), test_email: testEmail.trim() });
      if (res?.success) {
        Swal.fire({ title: "Test email sent", text: res.message, icon: "success" });
      } else {
        setErrors(res?.errors || {});
        Swal.fire({ title: "Test email failed", text: res?.message || "Unable to send the test email.", icon: "error" });
      }
    } catch (err: any) {
      Swal.fire({ title: "Test email failed", text: err?.message || "Unable to reach the server.", icon: "error" });
    } finally {
      setTesting(false);
    }
  };

  const provider = PROVIDERS.find((p) => p.value === form.provider);
  const err = (f: keyof Errors) => errors[f] && <span className="text-danger">{errors[f]}</span>;

  return (
    <>
      <Breadcrumbs mainTitle="Mail Configuration" parent="Settings" title="Mail Configuration" />
      <Fragment>
        <Card>
          <CardHeader style={{ backgroundColor: "#B4B4B3" }}>
            <Col></Col>
          </CardHeader>
          <CardBody className="py-2 my-25">
            {loading ? (
              <div className="text-center py-5"><Spinner /></div>
            ) : (
              <>
                {meta.is_configured ? (
                  <Alert color="success" className="mt-2">
                    All system emails (temporary passwords, password resets, notifications) are sent from <b>{form.from_email}</b>.
                    {meta.updated_at && <> Last updated {new Date(meta.updated_at).toLocaleString()}{meta.updated_by ? ` by ${meta.updated_by}` : ""}.</>}
                  </Alert>
                ) : (
                  <Alert color="warning" className="mt-2">
                    No mail account has been saved yet.
                    {meta.using_env_fallback ? " Emails are currently using the server's default (.env) account." : " Emails cannot be sent until a mail account is configured."}
                  </Alert>
                )}

                <Row>
                  <Col md={4} xs={12}>
                    <FormGroup>
                      <Label check>Mail Provider <span style={{ color: "red" }}> * </span></Label>
                      <Select
                        options={PROVIDERS}
                        value={PROVIDERS.find((p) => p.value === form.provider)}
                        onChange={(o: any) => onProviderChange(o?.value)}
                        className="react-select"
                        classNamePrefix="select"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4} xs={12}>
                    <FormGroup>
                      <Label check>SMTP Host <span style={{ color: "red" }}> * </span></Label>
                      <Input value={form.host} placeholder="e.g. smtp.example.com" onChange={(e) => set("host", e.target.value)} invalid={!!errors.host} />
                      {err("host")}
                    </FormGroup>
                  </Col>
                  <Col md={2} xs={6}>
                    <FormGroup>
                      <Label check>Port <span style={{ color: "red" }}> * </span></Label>
                      <Input type="number" value={form.port} onChange={(e) => set("port", e.target.value)} invalid={!!errors.port} />
                      {err("port")}
                    </FormGroup>
                  </Col>
                  <Col md={2} xs={6}>
                    <FormGroup>
                      <Label check>Security <span style={{ color: "red" }}> * </span></Label>
                      <Select
                        options={SECURITY_OPTIONS}
                        value={SECURITY_OPTIONS.find((o) => o.value === form.security)}
                        onChange={(o: any) => set("security", o?.value)}
                        className="react-select"
                        classNamePrefix="select"
                      />
                    </FormGroup>
                  </Col>

                  {provider?.hint && (
                    <Col xs={12}>
                      <Alert color="info" className="py-2">{provider.hint}</Alert>
                    </Col>
                  )}

                  <Col md={4} xs={12}>
                    <FormGroup>
                      <Label check>Username / Email</Label>
                      <Input value={form.username} autoComplete="off" placeholder="Login for the SMTP server" onChange={(e) => set("username", e.target.value)} />
                    </FormGroup>
                  </Col>
                  <Col md={4} xs={12}>
                    <FormGroup>
                      <Label check>Password / App Password</Label>
                      <InputGroup>
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={form.password}
                          autoComplete="new-password"
                          placeholder={hasPassword ? "Saved - leave blank to keep it" : "Enter the password"}
                          onChange={(e) => set("password", e.target.value)}
                          invalid={!!errors.password}
                        />
                        <InputGroupText className="cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                        </InputGroupText>
                      </InputGroup>
                      {err("password")}
                    </FormGroup>
                  </Col>
                  <Col md={4} xs={12}></Col>
                  <Col md={4} xs={12}>
                    <FormGroup>
                      <Label check>Sender Email (From) <span style={{ color: "red" }}> * </span></Label>
                      <Input value={form.from_email} placeholder="e.g. support@yourcompany.com" onChange={(e) => set("from_email", e.target.value)} invalid={!!errors.from_email} />
                      {err("from_email")}
                    </FormGroup>
                  </Col>
                  <Col md={4} xs={12}>
                    <FormGroup>
                      <Label check>Sender Name</Label>
                      <Input value={form.from_name} placeholder="e.g. Dentagenix IVP" onChange={(e) => set("from_name", e.target.value)} />
                    </FormGroup>
                  </Col>
                </Row>

                <hr />
                <Row className="align-items-end">
                  <Col md={4} xs={12}>
                    <FormGroup>
                      <Label check>Send a test email to</Label>
                      <Input value={testEmail} placeholder="you@example.com" onChange={(e) => { setTestEmail(e.target.value); setErrors((x) => ({ ...x, test_email: undefined })); }} invalid={!!errors.test_email} />
                      {err("test_email")}
                    </FormGroup>
                  </Col>
                  <Col md={3} xs={12}>
                    <FormGroup>
                      <Button color="primary" outline onClick={onTest} disabled={testing || saving}>
                        {testing ? "Sending..." : "Send Test Email"}
                      </Button>
                    </FormGroup>
                  </Col>
                  <Col className="text-end">
                    <FormGroup>
                      <Button color="success" onClick={onSave} disabled={saving || testing}>
                        {saving ? "Verifying..." : "Verify & Save"}
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}
          </CardBody>
        </Card>
      </Fragment>
    </>
  );
};

export default MailConfiguration;
